import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { CheckCircle2, Paperclip, Send, User, XCircle } from 'lucide-react';
import { api } from '@/lib/api';
import { fmtRelative } from '@/lib/format';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Input, Textarea } from '@/components/ui/Input';
import type { OutreachDraft } from '@/lib/api-types';

interface DraftReaderProps {
  draft: OutreachDraft;
  onDone: () => void;
  showSolicitationLink?: boolean;
}

export function DraftReader({ draft, onDone, showSolicitationLink = true }: DraftReaderProps) {
  const [subject, setSubject] = useState(draft.subject);
  const [body, setBody] = useState(draft.body);
  const [reason, setReason] = useState('');
  const [showReject, setShowReject] = useState(false);

  const approve = useMutation({
    mutationFn: () => api.approveDraft(draft.id, { subject, body }),
    onSuccess: onDone,
  });
  const reject = useMutation({
    mutationFn: () => api.rejectDraft(draft.id, reason),
    onSuccess: onDone,
  });

  const solicitationLabel =
    draft.solicitation_number ?? draft.rlp_opportunity_id.slice(0, 8);

  return (
    <article className="flex h-full flex-col" data-testid={`draft-card-${draft.id}`}>
      <header className="border-b border-rule bg-surface-sunk px-5 py-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="flex min-w-0 items-center gap-3">
            <div className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-gradient-to-br from-brand-400 to-brand-600 text-[0.75rem] font-bold text-white">
              {(draft.recipient_name ?? '?').slice(0, 2).toUpperCase()}
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-2 text-[0.8125rem]">
                <span className="font-semibold text-ink">{draft.recipient_name ?? '—'}</span>
                {draft.recipient_role && (
                  <Badge tone="neutral">{draft.recipient_role}</Badge>
                )}
              </div>
              <div className="truncate text-xs text-ink-muted">
                {draft.recipient_email ?? 'no email'}
              </div>
            </div>
          </div>
          <div className="flex flex-wrap justify-end gap-2">
            {!showReject ? (
              <>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setShowReject(true)}
                  data-testid={`reject-${draft.id}`}
                >
                  <XCircle className="h-3.5 w-3.5" /> Reject
                </Button>
                <Button
                  size="sm"
                  onClick={() => approve.mutate()}
                  disabled={approve.isPending}
                  data-testid={`approve-${draft.id}`}
                >
                  <Send className="h-3.5 w-3.5" />
                  {approve.isPending ? 'Sending…' : 'Approve & send'}
                </Button>
              </>
            ) : (
              <>
                <Button variant="ghost" size="sm" onClick={() => setShowReject(false)}>
                  Cancel
                </Button>
                <Button
                  variant="destructive"
                  size="sm"
                  disabled={reason.trim().length === 0 || reject.isPending}
                  onClick={() => reject.mutate()}
                  data-testid={`confirm-reject-${draft.id}`}
                >
                  Confirm reject
                </Button>
              </>
            )}
          </div>
        </div>

        <div className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-1 text-[0.75rem] text-ink-muted">
          <span className="inline-flex items-center gap-1.5">
            <User className="h-3 w-3" />
            Property · {draft.candidate_address ?? '—'}
          </span>
          {showSolicitationLink && (
            <span>
              Re:{' '}
              <Link
                to={`/rlps/${draft.rlp_opportunity_id}`}
                className="font-semibold text-brand-700 hover:text-brand-800"
              >
                {solicitationLabel}
              </Link>
            </span>
          )}
          <span>Drafted {fmtRelative(draft.created_at)}</span>
        </div>
      </header>

      <div className="flex flex-1 flex-col gap-4 p-5">
        <div className="flex flex-col gap-1.5">
          <label className="kicker">Subject</label>
          <Input
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            className="text-[0.9375rem] font-semibold"
            data-testid={`subject-${draft.id}`}
          />
        </div>
        <div className="flex flex-1 flex-col gap-1.5">
          <label className="kicker">Body</label>
          <Textarea
            className="min-h-[280px] flex-1 leading-relaxed"
            value={body}
            onChange={(e) => setBody(e.target.value)}
            data-testid={`body-${draft.id}`}
          />
        </div>
        {showReject && (
          <div className="flex flex-col gap-1.5">
            <label className="kicker text-danger-bold">Rejection reason</label>
            <Textarea
              placeholder="Why is this draft wrong? Helps tune the writer's prompt."
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              data-testid={`reason-${draft.id}`}
            />
          </div>
        )}
        {approve.isError && (
          <div className="rounded-lg border border-danger-border bg-danger-soft px-3 py-2 text-xs text-danger-bold">
            Approve failed: {approve.error instanceof Error ? approve.error.message : 'unknown'}
          </div>
        )}
        {reject.isError && (
          <div className="rounded-lg border border-danger-border bg-danger-soft px-3 py-2 text-xs text-danger-bold">
            Reject failed: {reject.error instanceof Error ? reject.error.message : 'unknown'}
          </div>
        )}
      </div>

      <footer className="flex items-center justify-between border-t border-rule bg-surface-sunk px-5 py-3">
        <div className="flex items-center gap-2 text-xs text-ink-muted">
          <Paperclip className="h-3.5 w-3.5" />
          Nothing sends until you approve.
        </div>
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            onClick={() => approve.mutate()}
            disabled={approve.isPending || showReject}
          >
            <CheckCircle2 className="h-3.5 w-3.5" />
            {approve.isPending ? 'Sending…' : 'Approve & send'}
          </Button>
        </div>
      </footer>
    </article>
  );
}
